<template>
    <nav class="bg-white flex flex-col relative">
        <!-- Main navigation bar -->
        <CollapseTransition name="nav-collapse" @before-enter="setCollapseAnimating(true)" @after-enter="setCollapseAnimating(false)" @before-leave="setCollapseAnimating(true)" @after-leave="setCollapseAnimating(false)">
            <div v-show="!collapseMainNav" class="px-4 md:px-10 xl:px-20 flex flex-col border-b-[0.5px] border-black-300">
                <slot :collapse-meta-nav="collapseMetaNav"
                      :set-collapse-animating="setCollapseAnimating"
                      :active-flyout-item="activeFlyoutItem"
                      :toggle-flyout="toggleFlyout"
                      :mobile-nav-opened="mobileNavOpened"
                      :toggle-mobile-nav="toggleMobileNav"
                      :global-nav-opened="globalNavOpened"
                      :open-global-nav="openGlobalNav"
                      :search-opened="searchOpened"
                      :toggle-search="toggleSearch" />
            </div>
        </CollapseTransition>

        <!-- Desktop flyout -->
        <Teleport :to="flyoutTeleportTarget" :disabled="!activeFlyoutItem">
            <div ref="flyoutContainer" class="absolute top-full inset-x-0 bg-white transition-[height] duration-300 ease-in-out px-20 z-50">
                <Transition name="flyout-fade" mode="out-in" @before-enter="updateFlyoutHeight" @after-leave="resetFlyoutHeight">
                    <slot name="flyout"
                          :active-flyout-item="activeFlyoutItem"
                          :active-sub-item="activeSubItem"
                          :toggle-sub-item="toggleSubItem"
                          :update-flyout-height="updateFlyoutHeight"
                          :focus-first="focusFirst" />
                </Transition>
            </div>
        </Teleport>

        <!-- Subsite navigation -->
        <slot name="subsite-nav"
              :active-subsite-flyout="activeSubsiteFlyout"
              :toggle-subsite-flyout="toggleSubsiteFlyout"
              :active-subsite-l2-item="activeSubsiteL2Item"
              :toggle-subsite-l2-item="toggleSubsiteL2Item"
              :mobile-subsite-nav-opened="mobileSubsiteNavOpened"
              :toggle-mobile-subsite-nav="toggleMobileSubsiteNav"
              :active-mobile-subsite-item="activeMobileSubsiteItem"
              :toggle-mobile-subsite-item="toggleMobileSubsiteItem"
              :active-mobile-subsite-l2-item="activeMobileSubsiteL2Item"
              :toggle-mobile-subsite-l2-item="toggleMobileSubsiteL2Item"
              :inert="!!activeFlyoutItem || mobileNavOpened"
              :update-subsite-flyout-height="updateSubsiteFlyoutHeight"
              :release-subsite-flyout-height="releaseSubsiteFlyoutHeight"
              :freeze-subsite-flyout-height="freezeSubsiteFlyoutHeight"
              :reset-subsite-flyout-height="resetSubsiteFlyoutHeight" />

        <!-- Mobile navigation menu -->
        <Transition name="nav-slide-down-fade">
            <div v-if="mobileNavOpened" class="lg:hidden fixed top-16 inset-x-0 bottom-0 px-4 md:px-10 bg-white border-t-[0.5px] border-black-300 overflow-y-auto">
                <slot name="mobile-flyout"
                      :active-mobile-flyout-item="activeMobileFlyoutItem"
                      :toggle-mobile-flyout="toggleMobileFlyout"
                      :active-mobile-sub-item="activeMobileSubItem"
                      :toggle-mobile-sub-item="toggleMobileSubItem"
                      :open-global-nav="openGlobalNav" />
            </div>
        </Transition>
        <!-- Locale guidance -->
        <slot name="locale-guidance" />

        <!-- Backdrop for open navigation layers -->
        <Teleport to="body">
            <Transition name="nav-fade">
                <div v-if="mobileNavOpened || mobileSubsiteNavOpened || activeFlyoutItem || activeSubsiteFlyout" class="fixed inset-0 bg-black/50" @click="closeAll"/>
            </Transition>
        </Teleport>

        <!-- Global navigation overlay -->
        <slot name="global-nav-overlay" />

        <!-- Search overlay -->
        <slot name="search-overlay" :search-opened="searchOpened" />
    </nav>
</template>

<script lang="ts">
import CollapseTransition from '../base/CollapseTransition.vue';
import debounce from 'lodash/debounce';
import { mapActions, mapWritableState } from 'pinia';
import { useNavigationStore } from '../../stores/navigation';
import { screens } from '../../plugins/breakpoints';

export default {
    components: { CollapseTransition },
    provide() {
        return {
            triggerOpenGlobalNav: () => this.openGlobalNav(),
        };
    },
    data() {
        return {
            scrollingDown: false,
            collapseAnimating: false,
            lastScrollY: 0,
            activeFlyoutItem: null as string | null,
            activeSubItem: null as string | null,
            activeSubsiteFlyout: null as string | null,
            activeSubsiteL2Item: null as string | null,
            mobileSubsiteNavOpened: false,
            mobileNavOpened: false,
            activeMobileSubsiteItem: null as string | null,
            activeMobileSubsiteL2Item: null as string | null,
            activeMobileFlyoutItem: null as string | null,
            activeMobileSubItem: null as string | null,
            isMobileScreen: window.innerWidth < screens.lg,
            resizeObserver: null as ResizeObserver | null,
        };
    },
    computed: {
        ...mapWritableState(useNavigationStore, ['searchOpened', 'globalNavOpened']),
        hasSubsiteNav(): boolean {
            return !!this.$slots['subsite-nav'];
        },
        /** When a subsite nav is present, the entire main nav collapses on scroll down unless search is open. */
        collapseMainNav(): boolean {
            return this.hasSubsiteNav && !this.isMobileScreen && this.scrollingDown && !this.searchOpened;
        },
        /** When no subsite nav is present, only the meta nav collapses on scroll down unless search is open. */
        collapseMetaNav(): boolean {
            return !this.hasSubsiteNav && this.scrollingDown && !this.searchOpened;
        },
        flyoutTeleportTarget(): string {
            return `#flyout-target-${this.activeFlyoutItem}`;
        },
    },
    watch: {
        activeFlyoutItem() {
            this.syncFocusTrap();
        },
        activeSubsiteFlyout() {
            this.syncFocusTrap();
        },
        mobileNavOpened() {
            this.syncFocusTrap();
            this.syncScrollLock();
        },
        mobileSubsiteNavOpened() {
            this.syncFocusTrap();
            this.syncScrollLock();
        },
        // Watchers for the state we need in the navigation store to calculate the content margin
        hasSubsiteNav: {
            handler(hasSubsiteNav: boolean) {
                this.setSubsiteNavExists(hasSubsiteNav);
            },
            immediate: true,
        },
        collapseMainNav: {
            handler(collapseMainNav: boolean) {
                this.setMainNavCollapsed(collapseMainNav);
            },
            immediate: true,
        },
        collapseMetaNav: {
            handler(collapseMetaNav: boolean) {
                this.setMetaNavCollapsed(collapseMetaNav);
            },
            immediate: true,
        },
    },
    mounted() {
        this.resetFlyoutHeight();
        this.lastScrollY = window.scrollY;
        window.addEventListener('scroll', this.onScroll, { passive: true });
        document.addEventListener('keydown', this.handleEscape);

        this.resizeObserver = new ResizeObserver(
            debounce(() => {
                this.handleViewportResize();
            }, 300),
        );
        this.resizeObserver.observe(document.body);
    },
    beforeUnmount() {
        window.removeEventListener('scroll', this.onScroll);
        document.removeEventListener('keydown', this.handleEscape);
        this.resizeObserver?.disconnect();
        this.toggleScrollLock(false);
    },
    methods: {
        ...mapActions(useNavigationStore, ['setMetaNavCollapsed', 'setMainNavCollapsed', 'setSubsiteNavExists']),
        setCollapseAnimating(value: boolean): void {
            this.collapseAnimating = value;
        },
        toggleFlyout(id: string, defaultSubItem?: string): void {
            const opening = id && this.activeFlyoutItem !== id;
            this.activeFlyoutItem = opening ? id : null;
            this.activeSubItem = opening && defaultSubItem ? defaultSubItem : null;
            this.activeSubsiteFlyout = null;
        },
        toggleSubItem(id: string): void {
            this.activeSubItem = this.activeSubItem === id ? null : id;
        },
        toggleMobileNav(initialFlyoutItem?: string, initialSubItem?: string): void {
            this.mobileNavOpened = !this.mobileNavOpened;
            if (this.mobileNavOpened) {
                this.activeMobileFlyoutItem = initialFlyoutItem || null;
                this.activeMobileSubItem = initialSubItem || null;
                this.searchOpened = false;
                this.mobileSubsiteNavOpened = false;
            }
        },
        toggleMobileFlyout(id: string | null): void {
            this.activeMobileFlyoutItem = this.activeMobileFlyoutItem === id ? null : id;
        },
        toggleMobileSubItem(id: string): void {
            this.activeMobileSubItem = this.activeMobileSubItem === id ? null : id;
        },
        toggleSubsiteFlyout(id: string, initialL2Item?: string): void {
            this.activeSubsiteFlyout = this.activeSubsiteFlyout === id ? null : id;
            this.activeSubsiteL2Item = initialL2Item || null;
            this.activeFlyoutItem = null;
        },
        toggleSubsiteL2Item(id: string): void {
            this.activeSubsiteL2Item = this.activeSubsiteL2Item === id ? null : id;
        },
        toggleMobileSubsiteNav(initialItem?: string, initialL2Item?: string): void {
            this.mobileSubsiteNavOpened = !this.mobileSubsiteNavOpened;
            if (this.mobileSubsiteNavOpened) {
                this.activeMobileSubsiteItem = initialItem || null;
                this.activeMobileSubsiteL2Item = initialL2Item || null;
                this.mobileNavOpened = false;
                this.searchOpened = false;
            }
        },
        toggleMobileSubsiteItem(id: string): void {
            this.activeMobileSubsiteItem = this.activeMobileSubsiteItem === id ? null : id;
            this.activeMobileSubsiteL2Item = null;
        },
        toggleMobileSubsiteL2Item(id: string): void {
            this.activeMobileSubsiteL2Item = this.activeMobileSubsiteL2Item === id ? null : id;
        },
        openGlobalNav(): void {
            this.globalNavOpened = true;
            this.searchOpened = false;
            this.activeFlyoutItem = null;
            this.activeSubsiteFlyout = null;
            this.mobileSubsiteNavOpened = false;
        },
        toggleSearch(): void {
            this.searchOpened = !this.searchOpened;
            if (this.searchOpened) {
                this.mobileNavOpened = false;
                this.activeFlyoutItem = null;
                this.activeSubsiteFlyout = null;
                this.mobileSubsiteNavOpened = false;
            }
        },
        updateFlyoutHeight(): void {
            this.$nextTick(() => {
                const container = this.$refs.flyoutContainer as HTMLElement;
                const el = container.firstElementChild as HTMLElement;
                if (el) {
                    container.style.height = el.offsetHeight + 'px';
                }
            });
        },
        resetFlyoutHeight(): void {
            if (!this.activeFlyoutItem) {
                const container = this.$refs.flyoutContainer as HTMLElement;
                container.style.height = '0';
            }
        },
        updateSubsiteFlyoutHeight(el: HTMLElement): void {
            this.$nextTick(() => {
                const wrapper = el.parentElement as HTMLElement;
                wrapper.style.height = el.offsetHeight + 'px';
            });
        },
        releaseSubsiteFlyoutHeight(el: HTMLElement): void {
            const wrapper = el.parentElement as HTMLElement;
            wrapper.style.height = 'auto';
        },
        freezeSubsiteFlyoutHeight(el: HTMLElement): void {
            const wrapper = el.parentElement as HTMLElement;
            wrapper.style.height = wrapper.offsetHeight + 'px';
        },
        resetSubsiteFlyoutHeight(el: HTMLElement): void {
            const wrapper = el.parentElement as HTMLElement;
            wrapper.style.height = '0';
        },
        focusFirst(el: HTMLElement): void {
            const focusable = el.querySelector<HTMLElement>('a, button, [tabindex]:not([tabindex="-1"])');
            focusable?.focus();
        },
        onScroll(): void {
            const currentScrollY = window.scrollY;
            const delta = currentScrollY - this.lastScrollY;

            // Ignore tiny scroll movements (scroll wheel arrest, trackpad bounce)
            if (Math.abs(delta) < 25) return;

            // During collapse/expand animation, track position but don't re-evaluate direction
            // to avoid flickering from layout-shift-induced scroll events
            if (!this.collapseAnimating) {
                this.scrollingDown = currentScrollY > 0 && delta > 0;
            }
            this.lastScrollY = currentScrollY;
        },
        handleViewportResize(): void {
            // close nav menu when going from mobile to desktop or from desktop to mobile
            const isMobileScreen = window.innerWidth < screens.lg;
            if (isMobileScreen !== this.isMobileScreen) {
                this.isMobileScreen = isMobileScreen;
                this.closeAll();
            }
        },
        toggleScrollLock(locked: boolean): void {
            document.body.style.overflow = locked ? 'hidden' : '';
        },
        syncScrollLock(): void {
            this.toggleScrollLock(this.mobileNavOpened || this.mobileSubsiteNavOpened);
        },
        trapFocusInNav(toggle: boolean): void {
            // we put inert on everything but the header when a flyout is open
            // to trap the focus inside the navigation
            const header = this.$el.closest('header');
            for (const sibling of header?.parentElement?.children ?? []) {
                if (sibling !== header) {
                    sibling.toggleAttribute('inert', toggle);
                }
            }
        },
        syncFocusTrap(): void {
            this.trapFocusInNav(!!this.activeFlyoutItem || !!this.activeSubsiteFlyout || this.mobileNavOpened || this.mobileSubsiteNavOpened);
        },
        closeAll(): void {
            this.toggleFlyout(null);
            this.activeSubsiteFlyout = null;
            this.mobileNavOpened = false;
            this.mobileSubsiteNavOpened = false;
        },
        handleEscape(event: KeyboardEvent): void {
            if (event.key === 'Escape') {
                this.closeAll();
            }
        },
    },
};
</script>
